// Ported from GeometryToolsPanel.jsx on 2026-10-09; geometry editing behavior is unchanged.
import { useId } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import type { GeometryToolsPanelProps, ToolButtonProps } from './types/geometryToolsPanel.types';
import '../../assets/alertMapEditor.scss';

const ToolButton = ({
  tooltip,
  ariaLabel = tooltip,
  selected,
  disabled,
  onClick,
  children,
}: ToolButtonProps) => {
  const tooltipId = useId();
  return (
    <OverlayTrigger
      placement="top"
      delay={{ show: 200, hide: 100 }}
      overlay={
        <Tooltip className="responder-tooltip" id={tooltipId}>
          {tooltip}
        </Tooltip>
      }
    >
      <span className="d-inline-flex w-100">
        <button
          type="button"
          className={`geometry-tool-button w-100 d-flex align-items-center justify-content-center${selected ? ' is-selected' : ''}`}
          aria-label={ariaLabel}
          aria-pressed={selected}
          disabled={disabled}
          onClick={onClick}
        >
          {children}
        </button>
      </span>
    </OverlayTrigger>
  );
};

const GeometryToolsPanel = ({
  activeMode,
  onSetMode,
  onUndo,
  onRedo,
  onCancel,
  canReshape = true,
  canResize = true,
  canRotate = true,
  canAddVertex = true,
  canEraseVertex = true,
  canUndo = false,
  canRedo = false,
}: GeometryToolsPanelProps) => (
  <div
    role="group"
    aria-label="Geometry editing tools"
    className="geometry-tools-grid d-grid gap-2 mb-2"
  >
    <ToolButton
      tooltip="Select and move shape"
      ariaLabel="Move"
      selected={activeMode === 'move'}
      onClick={() => onSetMode('move')}
    >
      {'\u2196'}
    </ToolButton>
    <ToolButton
      tooltip="Reshape by moving one vertex at a time"
      ariaLabel="Reshape"
      selected={activeMode === 'reshape'}
      disabled={!canReshape}
      onClick={() => onSetMode('reshape')}
    >
      {'\u270e'}
    </ToolButton>
    <ToolButton
      tooltip="Resize entire shape"
      ariaLabel="Resize"
      selected={activeMode === 'resize'}
      disabled={!canResize}
      onClick={() => onSetMode('resize')}
    >
      {'\u2922'}
    </ToolButton>
    <ToolButton
      tooltip="Rotate entire polygon"
      ariaLabel="Rotate"
      selected={activeMode === 'rotate'}
      disabled={!canRotate}
      onClick={() => onSetMode('rotate')}
    >
      {'\u21bb'}
    </ToolButton>
    <ToolButton
      tooltip="Add vertex on polygon border"
      ariaLabel="Add vertex"
      selected={activeMode === 'addVertex'}
      disabled={!canAddVertex}
      onClick={() => onSetMode('addVertex')}
    >
      {'\uff0b'}
    </ToolButton>
    <ToolButton
      tooltip="Erase vertex"
      ariaLabel="Erase vertex"
      selected={activeMode === 'eraseVertex'}
      disabled={!canEraseVertex}
      onClick={() => onSetMode('eraseVertex')}
    >
      {'\u232b'}
    </ToolButton>
    <ToolButton tooltip="Undo" disabled={!canUndo} onClick={onUndo}>
      {'\u21ba'}
    </ToolButton>
    <ToolButton tooltip="Redo" disabled={!canRedo} onClick={onRedo}>
      {'\u21bb'}
    </ToolButton>
    <ToolButton
      tooltip="Cancel and restore previous geometry"
      ariaLabel="Cancel"
      disabled={!canUndo}
      onClick={onCancel}
    >
      {'\u00d7'}
    </ToolButton>
  </div>
);

export default GeometryToolsPanel;
